import { Link } from "react-router-dom";
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FileText, Download, ExternalLink, ChevronDown, Info } from "lucide-react";
import PageMeta from "./PageMeta";

const PDF_FILE = "/assets/Cornwall-Recreational-Sea-Angling-Strategy-Draft.pdf";

const themes = [
  "Angler participation and leadership",
  "Recreational fisheries data and monitoring",
  "Review and monitoring of commercial netting",
  "Ghost nets and lost fishing gear",
  "Fish stocks and essential habitats",
  "Marine wildlife and bycatch",
  "Water user safety",
  "Sustainable commercial fishing",
  "Gold standard for recreational anglers",
  "Responsible bait gathering",
  "Sustainable bait supply and shared responsibility",
  "Mental health and individual wellbeing",
  "Responsible and accessible sea angling",
  "Enforcement and public accountability",
  "Delivery and review",
  "Proposed consultees and stakeholders",
];

export default function SeaAnglingStrategyPage() {
  const [showDocument, setShowDocument] = useState(false);

  return (
    <div className="page-shell min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <PageMeta
        title="Cornwall Recreational Sea Angling Strategy (Draft) | Redruth Sea Angling Association"
        description="Read the proposed Recreational Sea Angling Strategy for Cornwall, a community-led draft prepared by Cornish Recreational Sea Angling Voice for Cornwall IFCA."
        keywords="Cornwall sea angling strategy, recreational sea angling Cornwall, Cornwall IFCA, Cornish Recreational Sea Angling Voice, ghost nets, bait gathering"
        url="https://www.redruthseaanglingassociation.co.uk/sea-angling-strategy"
      />

      <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">

        <Link
          to="/"
          className="standalone-only inline-flex items-center gap-2 rounded-lg border border-cyan-300/40 bg-cyan-400/10 px-4 py-2 text-sm text-cyan-200 transition hover:bg-cyan-400/20"
        >
          ← Back
        </Link>

        <div className="mt-6 flex items-center gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-cyan-400/10 backdrop-blur">
            <FileText className="h-6 w-6 text-cyan-300" />
          </div>
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-300">Proposed Draft</p>
            <h1 className="heading-glow mt-1 text-3xl font-bold tracking-tight text-white sm:text-4xl">Recreational Sea Angling Strategy for Cornwall</h1>
          </div>
        </div>

        <div className="mt-8 space-y-8 rounded-[2rem] border border-white/10 bg-slate-900/95 p-6 backdrop-blur panel-lift sm:p-8">

          <p className="text-base leading-7 text-slate-300">
            Cornish Recreational Sea Angling Voice has prepared a proposed strategy for sea angling in Cornwall,
            for consideration by the Chief Fishery Officer of Cornwall IFCA. It sets out a practical programme
            for healthy fish populations, responsible sea angling, environmental protection and stronger angler
            participation.
          </p>

          <section>
            <h2 className="text-xl font-semibold text-white">The ambition</h2>
            <p className="mt-3 text-sm leading-7 text-slate-300">
              More fish and larger fish, thriving marine wildlife, sustainable coastal livelihoods and fair
              opportunities for people to enjoy sea angling. Anglers should help shape decisions, collect evidence
              and take responsibility for the future of the waters they fish.
            </p>
            <p className="mt-3 text-sm leading-7 text-slate-300">
              At its heart is a proposed RSAS working group, chaired by an experienced recreational sea angler,
              giving shore anglers, boat anglers, charter skippers, clubs, junior and disabled anglers an ongoing
              voice alongside fisheries managers, scientists, conservation bodies and commercial fishermen.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white">What the strategy covers</h2>
            <ol className="mt-4 grid gap-2 sm:grid-cols-2">
              {themes.map((theme, i) => (
                <li key={theme} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-300">
                  <span className="shrink-0 font-semibold text-cyan-300">{i + 1}</span>
                  <span>{theme}</span>
                </li>
              ))}
            </ol>
          </section>

          <section className="flex gap-4 rounded-xl border border-cyan-300/20 bg-cyan-400/5 p-6 panel-lift">
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-cyan-300" />
            <div className="text-sm leading-7 text-slate-300">
              <p>
                This is a community-led proposal, not an adopted policy of Cornwall IFCA. Proposed partners,
                responsibilities and timescales remain subject to agreement, and listing an organisation does not
                imply its endorsement.
              </p>
              <p className="mt-2 text-slate-400">Proposed draft for submission · Version 1.8 · 19 October 2026 · 17 pages</p>
            </div>
          </section>

          <div>
            <button
              type="button"
              onClick={() => setShowDocument((o) => !o)}
              aria-expanded={showDocument}
              className="flex w-full items-center justify-center gap-3 rounded-xl bg-cyan-500 px-6 py-5 text-slate-950 shadow-lg transition hover:bg-cyan-400 active:scale-95"
            >
              <FileText className="h-6 w-6 shrink-0" />
              <span className="text-base font-bold">{showDocument ? "Hide the document" : "Read the full document"}</span>
              <ChevronDown className={`h-5 w-5 shrink-0 transition-transform duration-300 ${showDocument ? "rotate-180" : ""}`} />
            </button>

            <AnimatePresence initial={false}>
              {showDocument && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className="overflow-hidden"
                >
                  {/* Phones don't render PDFs inline reliably, so they get the open button instead */}
                  <iframe
                    src={PDF_FILE}
                    title="Proposed Recreational Sea Angling Strategy for Cornwall"
                    className="mt-6 hidden w-full rounded-xl border border-white/10 bg-white sm:block"
                    style={{ height: "80vh" }}
                  />
                  <p className="mt-6 text-sm leading-7 text-slate-400 sm:hidden">
                    Tap below to open the document in your phone's PDF viewer.
                  </p>

                  <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                    <a
                      href={PDF_FILE}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-cyan-300/40 bg-cyan-400/10 px-5 py-3 text-sm font-semibold text-cyan-100 transition hover:bg-cyan-400/20"
                    >
                      <ExternalLink className="h-5 w-5 text-cyan-300" />
                      Open in a new tab
                    </a>
                    <a
                      href={PDF_FILE}
                      download
                      className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-cyan-300/40 bg-cyan-400/10 px-5 py-3 text-sm font-semibold text-cyan-100 transition hover:bg-cyan-400/20"
                    >
                      <Download className="h-5 w-5 text-cyan-300" />
                      Download PDF
                    </a>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

        </div>
      </div>
    </div>
  );
}
